import { Helmet } from '@modern-js/runtime/head';
import {
  useLoaderData,
  useLocation,
  useParams,
} from '@modern-js/runtime/router';
import { useEffect, useMemo, useState } from 'react';
import type React from 'react';

import type { SitePage } from '../common/interface';
import { AuthFailed, CodeblockRenderer, NotFound } from '../components';
import { useLayout } from '../contexts/LayoutContext';
import {
  generateBrandingLogoCssVariables,
  generateGoogleFontsUrl,
  generateSemanticCssFallback,
  generateSiteThemeCssVariables,
  generateSiteThemeFontUrl,
} from '../utils/siteTheme';
import {
  redirectUnauthenticatedUserToLogin,
  shouldRedirectUnauthenticatedNotFoundToLogin,
} from '../utils/unauthenticatedNotFoundRedirect';
// import Hero from '../components/Hero';
import type { LoaderData } from './$.data';
import { getDefaultPublicFaviconUrl } from '../config/apiConfig';
import { LAYOUT_SCRIPTS } from './layout-scripts-constants';

/**
 * HYDRATION-SAFE Catch-all route component
 *
 * FIXED: This version eliminates hydration issues by:
 * 1. Only accessing URLSearchParams on the client-side after hydration
 * 2. Using server-safe data (pathname, params) for initial render
 * 3. Progressive enhancement from server to client
 */
const CatchAllRoute = () => {
  const location = useLocation();
  const params = useParams();
  const { setShouldHideLayout, setHeaderVisible } = useLayout();

  // Exclude site-preview and session-expired routes handled by their own loader/components or parent app
  if (
    location.pathname.startsWith('/home/site-preview') ||
    location.pathname.startsWith('/site-preview') ||
    location.pathname.startsWith('/session-expired')
  ) {
    return null;
  }

  // Get SSR loader data - this is hydration-safe (can be null during hydration)
  const loaderData = useLoaderData() as LoaderData | null;

  // Client-only state - will be null during SSR and initial hydration
  const [hasHydrated, setHasHydrated] = useState(false);
  useEffect(() => setHasHydrated(true), []);

  // Client-side debug logging: log full raw and adapted payloads without restriction
  useEffect(() => {
    if (typeof window !== 'undefined' && loaderData) {
      try {
        // Log both adapted and raw payloads
        console.log(
          '[my-portal][client] adapted payload',
          loaderData.adaptedMyPortal ?? loaderData.siteData,
        );
        console.log('[my-portal][client] raw payload', loaderData.rawMyPortal);
        console.log(
          '[my-portal][client] headerVisible',
          (loaderData as { headerVisible?: boolean }).headerVisible,
        );
      } catch (error) {
        console.error('[BROWSER][$.tsx] Error in client-side logging:', error);
      }
    }
  }, [loaderData]);

  // Server-safe data that's available immediately
  const pagePath = location.pathname;
  const wildcardPath = params['*'] || '';

  // No client-only URL param dependencies needed anymore

  // Find matching page - works both server-side and client-side
  const getMatchingPage = () => {
    if (!loaderData?.siteData) return null;
    // Normalize a path to leading-slash, no trailing-slash (except root)
    const normalize = (p?: string) => {
      const v = (p || '').trim();
      if (v === '' || v === '/') return '/';
      const withSlash = v.startsWith('/') ? v : `/${v}`;
      return withSlash !== '/' ? withSlash.replace(/\/+$/, '') : '/';
    };

    // Only process routes under '/home'
    const isUnderHome = pagePath === '/home' || pagePath.startsWith('/home/');
    if (!isUnderHome) return null;

    // Strip '/home' base before matching against stored permalinks
    const normalizedPath = normalize(pagePath.replace(/^\/home(\/|$)/, '/'));

    // Try to find matching page by normalized permalink or homepage alias
    return (
      loaderData.siteData.data.find(page => {
        const permalink = normalize(page.permalink);
        return (
          permalink === normalizedPath ||
          (normalizedPath === '/' && page.homePage === true)
        );
      }) || null
    );
  };

  // Determine the current state for rendering
  const getCurrentState = () => {
    // Check for SSR loader errors first (available immediately)
    if (loaderData?.error) {
      if (loaderData.error.type === 'auth') {
        return 'auth-error';
      }
      return 'page-not-found';
    }

    // Check if loaderData is null (SSR loader not working)
    if (!loaderData) {
      return 'loading';
    }

    // Check if siteData is null - treat as 404 (missing siteId/tenantId or page not found)
    if (!loaderData.siteData) {
      return 'page-not-found';
    }

    // Check if we have site data and can find matching page (works in SSR)
    const matchingPage = getMatchingPage();
    if (loaderData.siteData && matchingPage) {
      return 'valid';
    }

    // Check if we have site data but no matching page
    if (loaderData.siteData && !matchingPage) {
      return 'page-not-found';
    }

    // If not hydrated yet and no data available, show hydrating state
    if (!hasHydrated) {
      return 'hydrating';
    }

    // Fallback state
    return 'loading';
  };

  const currentState = getCurrentState();

  const shouldLoginRedirectForNotFound =
    hasHydrated &&
    shouldRedirectUnauthenticatedNotFoundToLogin(loaderData, currentState);

  // Treat any non-valid state as error for head/meta injection guards
  const isErrorState = currentState !== 'valid';

  // Redirect to /session-expired when auth error is detected
  // EXCEPT for /home and /home/* routes (they support public pages mode)
  useEffect(() => {
    if (currentState === 'auth-error' && hasHydrated) {
      // Check if this is a /home or /home/* route
      const currentPath = window.location.pathname;
      const isHomeRoute =
        currentPath === '/home' || currentPath.startsWith('/home/');

      // Don't redirect /home routes - they support public pages mode
      if (isHomeRoute) {
        return;
      }

      // Prevent redirect loop - only redirect if not already navigating to session-expired
      if (!currentPath.includes('/session-expired')) {
        // Use window.location for full page navigation to exit SSR app context
        window.location.replace('/session-expired');
      }
    }
  }, [currentState, hasHydrated]);

  useEffect(() => {
    if (!hasHydrated) {
      return;
    }

    if (
      shouldRedirectUnauthenticatedNotFoundToLogin(loaderData, currentState)
    ) {
      redirectUnauthenticatedUserToLogin();
    }
  }, [currentState, hasHydrated, loaderData]);

  // Hide layout when showing error states and propagate header visibility.
  useEffect(() => {
    setShouldHideLayout(isErrorState);

    const headerVisibleFromLoader =
      loaderData && typeof loaderData === 'object'
        ? (loaderData as { headerVisible?: boolean }).headerVisible
        : undefined;
    setHeaderVisible(headerVisibleFromLoader ?? true);
  }, [isErrorState, loaderData, setHeaderVisible, setShouldHideLayout]);

  const layoutHtml = (
    loaderData && typeof loaderData === 'object'
      ? (loaderData as { layoutHtml?: string | null })?.layoutHtml
      : null
  ) as string | null | undefined;
  const layoutActive = Boolean(
    loaderData &&
      typeof loaderData === 'object' &&
      (loaderData as { layoutActive?: boolean }).layoutActive,
  );

  // Generate site theme CSS variables from loader data
  const siteThemeCss = useMemo(() => {
    const siteTheme = (
      loaderData?.siteData as unknown as { siteTheme?: unknown } | null
    )?.siteTheme;
    if (!siteTheme || typeof siteTheme !== 'object') return '';
    return generateSiteThemeCssVariables(
      siteTheme as Parameters<typeof generateSiteThemeCssVariables>[0],
    );
  }, [loaderData?.siteData]);

  // Semantic CSS fallback for Tailwind CDN (always needed)
  const semanticCssFallback = useMemo(
    () =>
      generateSemanticCssFallback(
        loaderData?.siteData?.siteTheme || loaderData?.siteTheme,
      ),
    [loaderData?.siteData?.siteTheme, loaderData?.siteTheme],
  );

  // Google Fonts URL for branded fonts from theming data
  const googleFontsUrl = useMemo(
    () => generateGoogleFontsUrl(loaderData?.themeVars),
    [loaderData?.themeVars],
  );

  // Custom font CSS URL from the tenant's uploaded font families
  const customFontCssUrl = loaderData?.customFontCssUrl ?? null;

  // Google Fonts URL for site theme font (if it's a Google Font)
  // Skip if customFontCssUrl is present (indicates a custom uploaded font)
  const siteThemeFontUrl = useMemo(() => {
    if (customFontCssUrl) return null; // Custom font CSS will handle this
    return generateSiteThemeFontUrl(loaderData?.siteTheme?.fontFamily);
  }, [loaderData?.siteTheme?.fontFamily, customFontCssUrl]);

  // Branding logo CSS variables from theming data
  const brandingLogoCss = useMemo(
    () => generateBrandingLogoCssVariables(loaderData?.themeVars),
    [loaderData?.themeVars],
  );

  const splitLayoutBody = (html: string | null | undefined) => {
    try {
      if (!html || typeof html !== 'string') return null;
      const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
      const body = bodyMatch ? bodyMatch[1] : html;
      const openMatch = body.match(/(<div[^>]*id=["']sw-page-root["'][^>]*>)/i);
      if (!openMatch) return null;
      const openTag = openMatch[1];
      const attrs: Record<string, string> = {};
      const attrRegex = /(\w[\w:-]*)\s*=\s*"([^"]*)"/g;
      let m: RegExpExecArray | null = attrRegex.exec(openTag);
      while (m !== null) {
        attrs[m[1]] = m[2];
        m = attrRegex.exec(openTag);
      }
      const rootId = attrs.id;
      const rootClass = attrs.class;
      const dataEntries = Object.entries(attrs).filter(([k]) =>
        k.startsWith('data-'),
      );
      return { root: { id: rootId, className: rootClass, data: dataEntries } };
    } catch {
      return null;
    }
  };

  const extractHeadScripts = (html: string | null | undefined): string[] => {
    try {
      if (!html || typeof html !== 'string') return [];
      const headMatch = html.match(/<head[^>]*>([\s\S]*?)<\/head>/i);
      const head = headMatch ? headMatch[1] : '';
      const scripts: string[] = [];
      const scriptRegex = /<script[^>]*src=["']([^"']+)["'][^>]*><\/script>/gi;
      let sm: RegExpExecArray | null = scriptRegex.exec(head);
      while (sm !== null) {
        scripts.push(sm[1]);
        sm = scriptRegex.exec(head);
      }
      return scripts;
    } catch {
      return [];
    }
  };

  // Shared helper: parse all attributes from a <link> tag's attribute string,
  // including boolean attributes like `crossorigin` that have no value.
  const parseLinkAttrs = (attrsStr: string): Record<string, string> => {
    const attrs: Record<string, string> = {};
    const kvRegex = /(\w[\w:-]*)\s*=\s*"([^"]*)"/g;
    let a = kvRegex.exec(attrsStr);
    while (a) {
      attrs[a[1]] = a[2];
      a = kvRegex.exec(attrsStr);
    }
    // Capture boolean attributes (no value) such as `crossorigin`
    const boolRegex = /(?:^|\s)(crossorigin|async|defer|nomodule)(?=\s|$|\/)/g;
    let b = boolRegex.exec(attrsStr);
    while (b) {
      attrs[b[1]] = '';
      b = boolRegex.exec(attrsStr);
    }
    return attrs;
  };

  const extractHeadStylesheets = (
    html: string | null | undefined,
  ): Array<Record<string, string>> => {
    try {
      if (!html || typeof html !== 'string') return [];
      const head = html.match(/<head[^>]*>([\s\S]*?)<\/head>/i)?.[1] || '';
      const stylesheets: Array<Record<string, string>> = [];
      const linkRegex = /<link([^>]*?)\/?>/gi;
      let lm: RegExpExecArray | null = linkRegex.exec(head);
      while (lm !== null) {
        const attrs = parseLinkAttrs(lm[1] || '');
        if ((attrs.rel || '').toLowerCase() === 'stylesheet') {
          stylesheets.push(attrs);
        }
        lm = linkRegex.exec(head);
      }
      return stylesheets;
    } catch {
      return [];
    }
  };

  const extractHeadInlineStyles = (
    html: string | null | undefined,
  ): string[] => {
    try {
      if (!html || typeof html !== 'string') return [];
      const headMatch = html.match(/<head[^>]*>([\s\S]*?)<\/head>/i);
      const head = headMatch ? headMatch[1] : '';
      const styles: string[] = [];
      const styleRegex = /<style[^>]*>([\s\S]*?)<\/style>/gi;
      let stm: RegExpExecArray | null = styleRegex.exec(head);
      while (stm !== null) {
        styles.push(stm[1]);
        stm = styleRegex.exec(head);
      }
      return styles;
    } catch {
      return [];
    }
  };

  const extractHeadInlineScripts = (
    html: string | null | undefined,
  ): string[] => {
    try {
      if (!html || typeof html !== 'string') return [];
      const headMatch = html.match(/<head[^>]*>([\s\S]*?)<\/head>/i);
      const head = headMatch ? headMatch[1] : '';
      const scripts: string[] = [];
      // Match inline scripts (without src attribute) that have content
      const scriptRegex =
        /<script(?![^>]*\ssrc=)([^>]*)>([\s\S]*?)<\/script>/gi;
      let sm: RegExpExecArray | null = scriptRegex.exec(head);
      while (sm !== null) {
        const content = sm[2]?.trim();
        if (content) {
          scripts.push(content);
        }
        sm = scriptRegex.exec(head);
      }
      return scripts;
    } catch {
      return [];
    }
  };

  // Extract <html> attributes like lang/dir/data-*
  const extractHtmlAttributes = (
    html: string | null | undefined,
  ): Record<string, string> => {
    try {
      if (!html || typeof html !== 'string') return {};
      const match = html.match(/<html([^>]*)>/i);
      const attrsStr = match ? match[1] : '';
      const attrs: Record<string, string> = {};
      const attrRegex = /(\w[\w:-]*)\s*=\s*"([^"]*)"/g;
      let m: RegExpExecArray | null = attrRegex.exec(attrsStr);
      while (m) {
        const key = m[1];
        const val = m[2];
        if (key === 'lang' || key === 'dir' || key.startsWith('data-')) {
          attrs[key] = val;
        }
        m = attrRegex.exec(attrsStr);
      }
      return attrs;
    } catch {
      return {};
    }
  };

  // Extract arbitrary <meta ...> tags from <head>
  const extractHeadMeta = (
    html: string | null | undefined,
  ): Array<Record<string, string>> => {
    try {
      if (!html || typeof html !== 'string') return [];
      const head = html.match(/<head[^>]*>([\s\S]*?)<\/head>/i)?.[1] || '';
      const metas: Array<Record<string, string>> = [];
      const metaRegex = /<meta([^>]*?)\/?>/gi;
      let m = metaRegex.exec(head);
      while (m) {
        const attrsStr = m[1] || '';
        const attrs: Record<string, string> = {};
        const attrRegex = /(\w[\w:-]*)\s*=\s*"([^"]*)"/g;
        let a = attrRegex.exec(attrsStr);
        while (a) {
          attrs[a[1]] = a[2];
          a = attrRegex.exec(attrsStr);
        }
        if (Object.keys(attrs).length) metas.push(attrs);
        m = metaRegex.exec(head);
      }
      return metas;
    } catch {
      return [];
    }
  };

  // Extract non-stylesheet <link ...> tags from <head> with allowlist
  const extractHeadLinks = (
    html: string | null | undefined,
  ): Array<Record<string, string>> => {
    try {
      if (!html || typeof html !== 'string') return [];
      const head = html.match(/<head[^>]*>([\s\S]*?)<\/head>/i)?.[1] || '';
      const links: Array<Record<string, string>> = [];
      const linkRegex = /<link([^>]*?)\/?>/gi;
      let m = linkRegex.exec(head);
      while (m) {
        const attrs = parseLinkAttrs(m[1] || '');
        const rel = (attrs.rel || '').toLowerCase();
        if (rel === 'stylesheet' || rel === 'base') {
          m = linkRegex.exec(head);
          continue;
        }
        if (
          // Disallow any icon links from API; allow preconnect, dns-prefetch, manifest
          rel === 'preconnect' ||
          rel === 'dns-prefetch' ||
          rel === 'manifest'
        ) {
          links.push(attrs);
        }
        m = linkRegex.exec(head);
      }
      return links;
    } catch {
      return [];
    }
  };

  // Extract <title> tag from <head>
  const extractHeadTitle = (html: string | null | undefined): string | null => {
    try {
      if (!html || typeof html !== 'string') return null;
      const head = html.match(/<head[^>]*>([\s\S]*?)<\/head>/i)?.[1] || '';
      const titleMatch = head.match(/<title[^>]*>([\s\S]*?)<\/title>/i);
      return titleMatch ? titleMatch[1].trim() : null;
    } catch {
      return null;
    }
  };

  const htmlAttrs = layoutActive ? extractHtmlAttributes(layoutHtml) : {};
  const apiMetasRaw = layoutActive ? extractHeadMeta(layoutHtml) : [];
  const apiLinksRaw = layoutActive ? extractHeadLinks(layoutHtml) : [];
  const apiTitle = layoutActive ? extractHeadTitle(layoutHtml) : null;
  const metaKey = (m: Record<string, string>) =>
    (m.name || m.property || m.charset || m['http-equiv'] || '').toLowerCase();
  const apiMetasUnique = Object.values(
    apiMetasRaw.reduce(
      (acc, m) => {
        const k = metaKey(m) || `${Object.keys(acc).length}`;
        acc[k] = m;
        return acc;
      },
      {} as Record<string, Record<string, string>>,
    ),
  );
  // Drop any API-provided charset meta; server/document defines utf-8
  const apiMetasNoCharset = apiMetasUnique.filter(m => !('charset' in m));
  const hasApiViewport = apiMetasUnique.some(
    m => (m.name || '').toLowerCase() === 'viewport',
  );
  const hasApiDescription = apiMetasUnique.some(
    m => (m.name || '').toLowerCase() === 'description',
  );
  // Icons are intentionally ignored from API

  const metaReactKey = (m: Record<string, string>) =>
    `${metaKey(m)}:${m.content || ''}:${m.charset || ''}:${m['http-equiv'] || ''}`;

  const linkReactKey = (l: Record<string, string>) =>
    `${(l.rel || 'link').toLowerCase()}:${l.href || ''}:${l.sizes || ''}:${l.type || ''}`;

  // Hydration-safe styles using style props
  const containerStyle = {
    // minHeight: '100vh',
    // display: 'flex',
    // flexDirection: 'column' as const,
    // alignItems: currentState === 'valid' ? 'stretch' : 'center',
    // justifyContent: currentState === 'valid' ? 'flex-start' : 'center',
    // padding: currentState === 'valid' ? '0' : '2rem',
    // background:
    //   currentState === 'valid'
    //     ? '#f8fafc'
    //     : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  };

  const contentStyle = {
    // width: '100%',
    // maxWidth: 'none',
    // padding: '0',
  };

  const cardStyle = {
    // background: 'white',
    // borderRadius: '16px',
    // padding: '3rem',
    // boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)',
    // textAlign: 'center' as const,
    // marginBottom: '2rem',
  };

  const getCardVariantStyle = () => {
    switch (currentState) {
      case 'hydrating':
      case 'loading':
        return { borderLeft: '8px solid #ffa500' };
      case 'auth-error':
        return { borderLeft: '8px solid #e53e3e' };
      case 'page-not-found':
        return { borderLeft: '8px solid #ed8936' };
      case 'valid':
        return { borderLeft: '8px solid #48bb78' };
      default:
        return {};
    }
  };

  const iconStyle = {
    fontSize: '4rem',
    marginBottom: '1.5rem',
  };

  const titleStyle = {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    color: '#2d3748',
    marginBottom: '1rem',
  };

  const descriptionStyle = {
    fontSize: '1.2rem',
    color: '#4a5568',
    marginBottom: '2rem',
    lineHeight: 1.6,
  };

  const routeInfoStyle = {
    background: '#f7fafc',
    padding: '1.5rem',
    borderRadius: '12px',
    marginBottom: '2rem',
    textAlign: 'left' as const,
  };

  const infoItemStyle = {
    marginBottom: '0.75rem',
    padding: '0.5rem',
    background: 'white',
    borderRadius: '6px',
    borderLeft: '4px solid #667eea',
  };

  const enhancedItemStyle = {
    ...infoItemStyle,
    borderLeft: '4px solid #48bb78',
  };

  const codeStyle = {
    background: '#edf2f7',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontFamily: 'monospace',
    color: '#2d3748',
  };

  const actionsStyle = {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'center',
    flexWrap: 'wrap' as const,
  };

  const baseButtonStyle = {
    padding: '1rem 2rem',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1.1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
  };

  const homeButtonStyle = {
    ...baseButtonStyle,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
  };

  const backButtonStyle = {
    ...baseButtonStyle,
    background: '#e2e8f0',
    color: '#2d3748',
  };

  const demoButtonStyle = {
    ...baseButtonStyle,
    background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
    color: 'white',
  };

  return (
    <div style={containerStyle}>
      <Helmet htmlAttributes={htmlAttrs}>
        {(layoutActive ? apiMetasNoCharset : []).map(m =>
          !isErrorState ? <meta key={metaReactKey(m)} {...m} /> : null,
        )}
        {!hasApiViewport && (
          <meta name="viewport" content="width=device-width, initial-scale=1" />
        )}
        <style>{`
          body {
            margin: 0;
            padding: 0;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
              'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
              sans-serif;
          }
        `}</style>
        <title>
          {(() => {
            const matchingPage = getMatchingPage();
            // Combine page name with layout title if both available
            if (apiTitle && currentState === 'valid' && matchingPage) {
              return `${matchingPage.name} - ${apiTitle}`;
            }
            // Use layout title if available
            if (apiTitle) {
              return apiTitle;
            }
            // Fall back to page-based title logic
            if (currentState === 'valid' && matchingPage) {
              return `${matchingPage.name}`;
            }
            if (currentState === 'valid') {
              return `Page: ${pagePath}`;
            }
            if (currentState === 'page-not-found') {
              return 'Page Not Found';
            }
            return 'Loading';
          })()}
        </title>
        {!hasApiDescription && (
          <meta
            name="description"
            content="Hydration-safe catch-all route implementation"
          />
        )}
        {(() => {
          const favicon = (loaderData as { lightFaviconUrl?: string | null })
            ?.lightFaviconUrl;
          const version = (loaderData as { tenantIdForFavicon?: string | null })
            ?.tenantIdForFavicon;
          const href =
            typeof favicon === 'string' && favicon.length > 0
              ? `${favicon}${version ? `?v=${encodeURIComponent(version)}` : ''}`
              : getDefaultPublicFaviconUrl();
          return [
            <link key="icon" rel="icon" href={href} />,
            <link key="shortcut-icon" rel="shortcut icon" href={href} />,
          ];
        })()}
        {!isErrorState &&
          (layoutActive ? apiLinksRaw : []).map(l => (
            <link key={linkReactKey(l)} {...l} />
          ))}
        {/* Google Fonts for branded fonts */}
        {googleFontsUrl && [
          <link
            key="preconnect-google"
            rel="preconnect"
            href="https://fonts.googleapis.com"
          />,
          <link
            key="preconnect-gstatic"
            rel="preconnect"
            href="https://fonts.gstatic.com"
            crossOrigin="anonymous"
          />,
          <link key="google-fonts" href={googleFontsUrl} rel="stylesheet" />,
        ]}
        {/* Custom font CSS from tenant-uploaded font families */}
        {customFontCssUrl && (
          <link
            key="custom-font-css"
            href={customFontCssUrl}
            rel="stylesheet"
          />
        )}
        {/* Google Fonts for site theme font (if different from branded and is a Google Font) */}
        {siteThemeFontUrl && siteThemeFontUrl !== googleFontsUrl && (
          <link
            key="google-fonts-site-theme"
            href={siteThemeFontUrl}
            rel="stylesheet"
          />
        )}
        {/* Site theme CSS variables for consistent branding */}
        {siteThemeCss && <style data-site-theme="true">{siteThemeCss}</style>}
        {/* Branding logo CSS variables */}
        {brandingLogoCss && (
          <style data-branding-logos="true">{brandingLogoCss}</style>
        )}
        {/* Semantic CSS fallback for Tailwind semantic classes (bg-primary, text-foreground, etc.) */}
        <style>{semanticCssFallback}</style>
        {/* Layout stylesheets - load before inline styles */}
        {(layoutActive ? extractHeadStylesheets(layoutHtml) : []).map(attrs => (
          <link key={linkReactKey(attrs)} {...attrs} />
        ))}
        {/* Layout external scripts */}
        {(layoutActive ? extractHeadScripts(layoutHtml) : []).map(src => (
          <script key={src} src={src} />
        ))}
        {/* Layout inline scripts */}
        {!isErrorState &&
          (layoutActive ? extractHeadInlineScripts(layoutHtml) : []).map(
            (scriptContent, index) => {
              const key = `layout-inline-script-${index}-${scriptContent.length}-${scriptContent.charCodeAt(0) || 0}`;
              return <script key={key}>{scriptContent}</script>;
            },
          )}
        {/* Layout inline styles */}
        {(layoutActive ? extractHeadInlineStyles(layoutHtml) : []).map(css => {
          const key = `layout-style-${css.length}-${css.charCodeAt(0) || 0}-${css.charCodeAt(css.length - 1) || 0}`;
          return <style key={key}>{css}</style>;
        })}
        {/* Include layout scripts */}
        {!isErrorState && <script>{LAYOUT_SCRIPTS.pendo}</script>}
        {/* Inject Tailwind LAST to ensure its styles have highest priority */}
        {!layoutActive && <script src="https://cdn.tailwindcss.com" />}
      </Helmet>

      <main style={contentStyle}>
        {currentState === 'valid' &&
          (() => {
            const matchingPage = getMatchingPage();
            if (!matchingPage) return null;
            const parts = splitLayoutBody(layoutHtml || null);
            if (!parts) {
              return <SinglePageRenderer page={matchingPage} />;
            }
            const rootProps: Record<string, string> = {};
            if (parts.root.id) rootProps.id = parts.root.id;
            if (layoutActive && parts.root.className)
              rootProps.className = parts.root.className;
            for (const [k, v] of parts.root.data) rootProps[k] = v;
            return (
              <>
                <div {...rootProps}>
                  <SinglePageRenderer page={matchingPage} />
                </div>
              </>
            );
          })()}

        {/* Authentication Error */}
        {currentState === 'auth-error' && <AuthFailed loginHref="/" />}

        {/* Page Not Found Error */}
        {currentState === 'page-not-found' &&
          !shouldLoginRedirectForNotFound && (
            <NotFound variant="page-not-found" />
          )}
      </main>
    </div>
  );
};

/**
 * Single Page Renderer Component
 * Renders a complete page with all its sections using the 3-field structure:
 * - content: HTML content
 * - js: EJS template code (processed server-side into processedHtml)
 * - css: Custom CSS styles (scoped to sections)
 */
interface SinglePageRendererProps {
  page: SitePage;
}

const SinglePageRenderer: React.FC<SinglePageRendererProps> = ({ page }) => {
  return (
    <div
      style={{
        width: '100%',
        maxWidth: 'none',
        margin: '0',
      }}
    >
      {/* Page Content - Render all sections */}
      <div
        style={{
          padding: '0',
        }}
      >
        {page.sections.length > 0 ? (
          <>
            {page.sections.map((section, index) => (
              <CodeblockRenderer
                key={section.id || `section-${index}`}
                section={section}
                showDebugInfo={false}
              />
            ))}
          </>
        ) : (
          <div
            style={{
              padding: '3rem',
              textAlign: 'center',
              color: '#6b7280',
            }}
          >
            <div
              style={{
                fontSize: '3rem',
                marginBottom: '1rem',
              }}
            >
              📄
            </div>
            <p style={{ margin: '0', fontSize: '1.125rem', fontWeight: '500' }}>
              This page has no content sections yet.
            </p>
            <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem' }}>
              Sections will appear here once they are added to the page.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CatchAllRoute;
